import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { SUPPORTED_LANGS, type SupportedLang } from '@/lib/i18n-safe';

/**
 * Injects <link rel="alternate" hreflang="..."> tags for every supported language.
 * Only active on public /:lang/* routes — no-op on auth-zone routes (no :lang segment).
 *
 * Why manage in JS: the app is a SPA with no SSR, so there is no server-side
 * opportunity to set hreflang. This hook fires on route change and keeps the
 * <head> in sync. Search engines that crawl SPAs honour these tags.
 */
export function useHreflang(currentLang: string | undefined) {
    const { pathname } = useLocation();

    useEffect(() => {
        if (!currentLang || !(SUPPORTED_LANGS as readonly string[]).includes(currentLang)) return;

        // Remove any previously injected hreflang tags before re-injecting.
        document.querySelectorAll('link[data-hreflang]').forEach(el => el.remove());

        const origin = window.location.origin;

        // Build the path segment after the lang prefix.
        // e.g. /fr/about → /about, then re-prefix per language.
        const langPrefixPattern = new RegExp(`^/(${SUPPORTED_LANGS.join('|')})(/?)`);
        const pathWithoutLang = pathname.replace(langPrefixPattern, '/');

        (SUPPORTED_LANGS as readonly SupportedLang[]).forEach(lang => {
            const link = document.createElement('link');
            link.rel = 'alternate';
            link.hreflang = lang;
            link.href = `${origin}/${lang}${pathWithoutLang === '/' ? '' : pathWithoutLang}`;
            link.dataset.hreflang = lang;
            document.head.appendChild(link);
        });

        // x-default points to the canonical fallback (EN).
        const xDefault = document.createElement('link');
        xDefault.rel = 'alternate';
        xDefault.hreflang = 'x-default';
        xDefault.href = `${origin}/en${pathWithoutLang === '/' ? '' : pathWithoutLang}`;
        xDefault.dataset.hreflang = 'x-default';
        document.head.appendChild(xDefault);

        return () => {
            document.querySelectorAll('link[data-hreflang]').forEach(el => el.remove());
        };
    }, [pathname, currentLang]);
}
